import { useCallback, useEffect, useRef } from "react";
import { VERSION_CHECK_MIN_INTERVAL_MS } from "../common/constants";

// A tab left open across a deploy keeps running the JS it already loaded —
// server-side cache-busting on index.html only helps *new* page loads, it
// can't reach into a tab that's already open. This hook closes that gap: it
// checks the build version the moment a human actually comes back to the
// tab (focus / visibility change) rather than on a fixed timer, so idle
// background tabs generate zero extra traffic, and the check still fires
// right when it matters (e.g. right after the laptop wakes from sleep).
export const useVersionCheck = () => {
  const lastCheckedAt = useRef(0);
  const isChecking = useRef(false);

  const checkVersion = useCallback(async () => {
    const runningVersion = process.env.REACT_APP_RELEASE_VERSION;
    if (!runningVersion) {
      return; // no version baked into this build — nothing to compare against
    }

    const now = Date.now();
    if (isChecking.current || now - lastCheckedAt.current < VERSION_CHECK_MIN_INTERVAL_MS) {
      return;
    }
    isChecking.current = true;
    lastCheckedAt.current = now;

    try {
      const response = await fetch(`/version.json?ts=${now}`, {
        cache: "no-store",
      });
      if (!response.ok) {
        return;
      }
      const data = await response.json();
      if (data?.version && data.version !== runningVersion) {
        window.location.reload();
      }
    } catch {
      // Network hiccup checking for a new version isn't worth acting on —
      // the next focus/visibility trigger will try again.
    } finally {
      isChecking.current = false;
    }
  }, []);

  useEffect(() => {
    const handleTabActive = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };

    window.addEventListener("focus", handleTabActive);
    document.addEventListener("visibilitychange", handleTabActive);

    return () => {
      window.removeEventListener("focus", handleTabActive);
      document.removeEventListener("visibilitychange", handleTabActive);
    };
  }, [checkVersion]);
};
